import type { TileRef } from "../document/types.ts";
import type { DaedalusGameAdapter } from "./adapter.ts";
import { frameNamed, type EditorCatalogue } from "./catalogue.ts";
import type { ObjectTypeDefinition } from "./objects.ts";

/**
 * The strips a game suggests for a type's animations as a behaviour is switched on for a prop (ADR-096 decision 40),
 * resolved against the catalogue: the game names a strip, Daedalus finds it.
 */

/**
 * The strips the game suggests for a type's animations: the game is asked once for each animation the type's
 * transitions name, with the placed frame's name and the names of its tileset's frames, and each name it gives is
 * resolved against the catalogue, preferring the sheet the placed frame came from.
 *
 * @param definition The registered type.
 * @param placed The frame the prop carries.
 * @param catalogue The world's catalogue.
 * @param suggest The game's hook, or undefined when it registers none.
 * @returns The animations by name, empty when the game suggests none, the catalogue does not name the placed frame
 *   or it lists none of the suggested strips.
 */
export function suggestedAnimations(definition: ObjectTypeDefinition, placed: TileRef, catalogue: EditorCatalogue, suggest: DaedalusGameAdapter["suggestAnimation"]): Record<string, TileRef> {
  const animations: Record<string, TileRef> = {};
  const name = catalogue.props.find((f) => f.tileset === placed.tileset && f.tile === placed.tile)?.name;
  if (suggest === undefined || name === undefined) {
    return animations;
  }
  const names = catalogue.props.filter((f) => f.tileset === placed.tileset).map((f) => f.name);
  const asked = new Set<string>();
  for (const transition of definition.transitions) {
    const plays = transition.plays;
    if (plays === undefined || asked.has(plays)) {
      continue;
    }
    asked.add(plays);
    const suggestion = suggest(definition.type, plays, name, names);
    const frame = suggestion === undefined ? undefined : frameNamed(catalogue, suggestion, placed.tileset);
    if (frame !== undefined) {
      animations[plays] = frame;
    }
  }
  return animations;
}
